import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  CartErrorEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Map<string, number>} */
  #quantityUpdateTimers = new Map();

  /** @type {Map<string, QuantityUpdateConfig>} */
  #scheduledQuantityUpdates = new Map();

  /** @type {Map<string, QuantityUpdateConfig>} */
  #pendingQuantityUpdates = new Map();

  #isProcessingQuantityUpdates = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);

    for (const timer of this.#quantityUpdateTimers.values()) {
      window.clearTimeout(timer);
    }

    this.#quantityUpdateTimers.clear();
    this.#scheduledQuantityUpdates.clear();
    this.#pendingQuantityUpdates.clear();
    this.#setLineLoading(null, false);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const quantitySelector = event.target.closest('cart-quantity-selector-component');
    if (!(quantitySelector instanceof HTMLElement) || !this.contains(quantitySelector)) return;

    const quantityInput = quantitySelector.querySelector('input[data-cart-line]');
    if (!(quantityInput instanceof HTMLInputElement) || quantityInput.disabled) return;

    const lineItemRow = quantitySelector.closest('[data-key]');
    const row = lineItemRow instanceof HTMLTableRowElement ? lineItemRow : null;

    const { quantity, cartLine: line } = event.detail;
    const cartKey = quantityInput.dataset.cartKey || row?.dataset.key;
    const parsedQuantity = Number(quantity);
    const previousQuantity = Number.parseInt(quantityInput.defaultValue, 10);

    if ((!cartKey && !line) || !Number.isFinite(parsedQuantity)) return;

    if (parsedQuantity === 0 && line) {
      return this.onLineItemRemove(line);
    }

    if (parsedQuantity === previousQuantity) {
      quantitySelector.updateButtonStates?.();
      this.#setLineLoading(row, false);
      return;
    }

    const updateId = cartKey || `line:${line}`;
    const existingTimer = this.#quantityUpdateTimers.get(updateId);
    if (existingTimer) window.clearTimeout(existingTimer);

    this.#hideCartError(row);
    this.#setLineLoading(row, true);

    const config = {
      line,
      cartKey,
      quantity: parsedQuantity,
      previousQuantity: Number.isFinite(previousQuantity) ? previousQuantity : parsedQuantity,
      action: 'change',
      row,
    };
    this.#scheduledQuantityUpdates.set(updateId, config);

    const timer = window.setTimeout(() => {
      this.#quantityUpdateTimers.delete(updateId);
      const scheduledUpdate = this.#scheduledQuantityUpdates.get(updateId);
      this.#scheduledQuantityUpdates.delete(updateId);
      if (!scheduledUpdate) return;

      this.#pendingQuantityUpdates.set(updateId, scheduledUpdate);
      this.#processPendingQuantityUpdates();
    }, 300);

    this.#quantityUpdateTimers.set(updateId, timer);
  };

  /**
   * Processes quantity updates one at a time so cart responses cannot arrive out of order.
   */
  async #processPendingQuantityUpdates() {
    if (this.#isProcessingQuantityUpdates) return;

    this.#isProcessingQuantityUpdates = true;

    try {
      while (this.#pendingQuantityUpdates.size > 0) {
        const nextUpdate = this.#pendingQuantityUpdates.entries().next().value;
        if (!nextUpdate) break;

        const [updateId, config] = nextUpdate;
        this.#pendingQuantityUpdates.delete(updateId);
        await this.updateQuantity(config);
      }
    } finally {
      this.#isProcessingQuantityUpdates = false;
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const lineItemRow = this.#findLineItemRow(undefined, line);

    for (const timer of this.#quantityUpdateTimers.values()) {
      window.clearTimeout(timer);
    }
    this.#quantityUpdateTimers.clear();
    this.#scheduledQuantityUpdates.clear();
    this.#pendingQuantityUpdates.clear();

    const updatePromise = this.updateQuantity({
      line,
      cartKey: lineItemRow?.dataset.key,
      quantity: 0,
      previousQuantity: Number.parseInt(
        lineItemRow?.querySelector('input[data-cart-line]')?.defaultValue ?? '0',
        10
      ),
      action: 'clear',
      row: lineItemRow,
    });

    if (!lineItemRow) return updatePromise;

    const rowsToRemove = [
      lineItemRow,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === lineItemRow.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return updatePromise;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    return updatePromise;
  }

  /**
   * Updates the quantity.
   * @param {QuantityUpdateConfig} config - The config.
   */
  async updateQuantity(config) {
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, cartKey, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.#findLineItemRow(cartKey, line) || config.row;
    const quantityInput = lineItemRow?.querySelector('input[data-cart-line]');

    if (quantityInput instanceof HTMLInputElement && quantity > 0) {
      quantityInput.value = quantity.toString();
    }

    this.#hideCartError(lineItemRow);
    this.#setLineLoading(lineItemRow, true);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const requestData = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (cartKey) {
      requestData.id = cartKey;
    } else {
      requestData.line = line;
    }

    const body = JSON.stringify(requestData);
    const abortController = new AbortController();
    const timeout = window.setTimeout(() => abortController.abort(), 15000);
    let cartMutationSucceeded = false;

    cartTotal?.shimmer();

    try {
      const response = await fetch(
        `${Theme.routes.cart_change_url}`,
        {
          ...fetchConfig('json', { body }),
          signal: abortController.signal,
        }
      );
      const responseText = await response.text();
      let parsedResponseText;

      try {
        parsedResponseText = JSON.parse(responseText);
      } catch {
        throw new Error('Cart update returned an invalid response.');
      }

      resetShimmer(this);

      if (!response.ok || parsedResponseText.errors) {
        this.#handleCartError(config, parsedResponseText);
        this.#dispatchCartError(parsedResponseText);
        return;
      }

      cartMutationSucceeded = true;
      const sectionHTML = parsedResponseText.sections?.[this.sectionId];
      const newSectionHTML = sectionHTML
        ? new DOMParser().parseFromString(sectionHTML, 'text/html')
        : null;

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount
        ? parseInt(newCartHiddenItemCount, 10)
        : Number(parsedResponseText.item_count ?? 0);

      // Update data-cart-quantity for product-page selectors.
      this.#updateQuantitySelectors(parsedResponseText);

      await this.#renderUpdatedCartSection(sectionHTML);
      this.#reapplyQueuedQuantityValues();
      this.#updateCartQuantitySelectorButtonStates();

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
    } catch (error) {
      console.error(error);

      if (!cartMutationSucceeded) {
        this.#handleCartError(config, {
          errors:
            error instanceof DOMException && error.name === 'AbortError'
              ? 'The cart update took too long. Please try again.'
              : 'The cart could not be updated. Please try again.',
        });
      }

      this.#dispatchCartError({
        message: error instanceof Error ? error.message : 'Cart update failed.',
        errors: error instanceof Error ? error.message : 'Cart update failed.',
      });
    } finally {
      window.clearTimeout(timeout);
      resetShimmer(this);
      this.#enableCartItems();
      this.#setLineLoading(lineItemRow, false);
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Morphs the returned cart section, with a fresh section render as a fallback.
   * @param {string | undefined} sectionHTML - The section HTML returned by the cart API.
   */
  async #renderUpdatedCartSection(sectionHTML) {
    if (sectionHTML) {
      try {
        await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });
        return;
      } catch (error) {
        console.error(error);
      }
    }

    await sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      mode: this.isDrawer ? 'hydration' : 'full',
    });
  }

  /**
   * Restores optimistic values that are still waiting behind the current request.
   */
  #reapplyQueuedQuantityValues() {
    const queuedUpdates = new Map([
      ...this.#scheduledQuantityUpdates,
      ...this.#pendingQuantityUpdates,
    ]);

    for (const config of queuedUpdates.values()) {
      const lineItemRow = this.#findLineItemRow(config.cartKey, config.line);
      const quantitySelector = lineItemRow?.querySelector('cart-quantity-selector-component');
      const quantityInput = quantitySelector?.querySelector('input[data-cart-line]');

      if (!(quantityInput instanceof HTMLInputElement)) continue;

      quantityInput.value = config.quantity.toString();
      quantitySelector.updateButtonStates?.();
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {QuantityUpdateConfig} config - The failed update.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string | Object} parsedResponseText.errors - The errors.
   */
  #handleCartError(config, parsedResponseText) {
    const lineItemRow = this.#findLineItemRow(config.cartKey, config.line) || config.row;
    const quantitySelector = lineItemRow?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input[data-cart-line]');

    if (quantityInput instanceof HTMLInputElement && Number.isFinite(config.previousQuantity)) {
      quantityInput.value = config.previousQuantity.toString();
      quantitySelector.updateButtonStates?.();
    }

    const cartItemError = lineItemRow?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = lineItemRow?.querySelector('.cart-items__error');

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = this.#getCartErrorMessage(parsedResponseText);
    }

    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  }

  /**
   * Dispatches the standard cart error event.
   * @param {Object} parsedResponseText - The failed response.
   */
  #dispatchCartError(parsedResponseText) {
    this.dispatchEvent(
      new CartErrorEvent(
        this.sectionId,
        parsedResponseText.message || this.#getCartErrorMessage(parsedResponseText),
        parsedResponseText.description || '',
        parsedResponseText.errors || ''
      )
    );
  }

  /**
   * Gets a readable cart error message.
   * @param {Object} parsedResponseText - The failed response.
   * @returns {string}
   */
  #getCartErrorMessage(parsedResponseText) {
    if (typeof parsedResponseText.errors === 'string') return parsedResponseText.errors;
    if (typeof parsedResponseText.description === 'string') return parsedResponseText.description;
    if (typeof parsedResponseText.message === 'string') return parsedResponseText.message;
    return 'The cart could not be updated. Please try again.';
  }

  /**
   * Hides an existing error before a new update.
   * @param {HTMLTableRowElement | null | undefined} lineItemRow - The active cart row.
   */
  #hideCartError(lineItemRow) {
    const cartItemError = lineItemRow?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = lineItemRow?.querySelector('.cart-items__error');

    if (cartItemError instanceof HTMLElement) cartItemError.textContent = '';
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.add('hidden');
  }

  /**
   * Finds the current cart row by stable key, with line number as a fallback.
   * @param {string | undefined} cartKey - The line item key.
   * @param {number | undefined} line - The line number.
   * @returns {HTMLTableRowElement | undefined}
   */
  #findLineItemRow(cartKey, line) {
    const rows = Array.from(this.querySelectorAll('[ref="cartItemRows[]"]')).filter(
      (row) => row instanceof HTMLTableRowElement
    );

    if (cartKey) {
      const matchingRow = rows.find((row) => row.dataset.key === cartKey);
      if (matchingRow instanceof HTMLTableRowElement) return matchingRow;
    }

    const fallbackRow = line ? rows[line - 1] : undefined;
    return fallbackRow instanceof HTMLTableRowElement ? fallbackRow : undefined;
  }

  /**
   * Starts or stops the row loader through one explicit lifecycle.
   * @param {HTMLTableRowElement | null | undefined} row - The active cart row.
   * @param {boolean} isLoading - Whether the row is loading.
   */
  #setLineLoading(row, isLoading) {
    document.dispatchEvent(
      new CustomEvent(isLoading ? 'sarky:cart-row-loading-start' : 'sarky:cart-row-loading-stop', {
        detail: { row },
      })
    );
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantitiesByVariant = new Map();

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of this.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/**
 * @typedef {Object} QuantityUpdateConfig
 * @property {number | undefined} line
 * @property {string | undefined} cartKey
 * @property {number} quantity
 * @property {number} previousQuantity
 * @property {'change' | 'clear'} action
 * @property {HTMLTableRowElement | null | undefined} row
 */
